import classNames from 'classnames';
import React, { useState } from 'react';
import type { Accept, FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import LoadingIndicator from './LoadingIndicator';
import SVGIcon from './SVGIcon';
import * as styles from './Uploader.module.scss';
import { useAppDispatch } from '../app/redux/useAppSelector';
import fileIcon from '../icons/non-prefilled/28x28/dropzoneFile.svg';
import imageIcon from '../icons/non-prefilled/28x28/dropzoneImage.svg';
import { UIActions } from '../ui/uiSlice';
import isFileOverlarge from '../utils/network/isFileOverlarge';

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const UPLOADER_STATUS = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  ACCEPT: 'accept',
  REJECT: 'reject',
};

interface BaseUploaderProps {
  image: boolean;
  status: (typeof UPLOADER_STATUS)[keyof typeof UPLOADER_STATUS];
  subject: string;
  info?: React.ReactNode;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const BaseUploader = ({
  image,
  status,
  subject,
  info,
}: BaseUploaderProps) => (
  <div
    className={classNames(styles.uploader, {
      [styles.uploaderAccept]: status === UPLOADER_STATUS.ACCEPT,
      [styles.uploaderReject]: status === UPLOADER_STATUS.REJECT,
    })}
  >
    {status === UPLOADER_STATUS.UPLOADING ? (
      <div className={styles.uploaderIcon}>
        <LoadingIndicator delay={0} />
      </div>
    ) : (
      <SVGIcon className={styles.uploaderIcon}>
        {image ? imageIcon : fileIcon}
      </SVGIcon>
    )}
    <div>
      {status === UPLOADER_STATUS.UPLOADING ? (
        'Uploading…'
      ) : (
        <>
          <div>
            Drop {subject}{' '}
            <span className={styles.wrappingGroup}>
              here or click to open file selector
            </span>
          </div>
          {!!info && <div className={styles.info}>{info}</div>}
        </>
      )}
    </div>
  </div>
);

interface UploaderProps {
  accept?: Accept;
  maxSizeMB?: number;
  multiple?: boolean;
  onDrop: (files: File[]) => void | Promise<void>;
  subject: string;
  onMaxSizeError?: () => void;
  info?: React.ReactNode;
  fullHeight?: boolean;
  image: boolean;
}

const Uploader = ({
  accept,
  maxSizeMB = 25,
  multiple = false,
  subject,
  onDrop,
  onMaxSizeError,
  info,
  fullHeight,
  image,
}: UploaderProps) => {
  const [uploading, setUploading] = useState(false);
  const dispatch = useAppDispatch();

  const handleDrop = (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
  ) => {
    fileRejections.forEach(({ file }) => {
      if (isFileOverlarge(file, maxSizeMB)) {
        if (onMaxSizeError) {
          onMaxSizeError();
        } else {
          dispatch(
            UIActions.pushToast({
              level: 'error',
              message: `Oh no! ${file.name} failed to upload.It exceeded the max allowed size: ${maxSizeMB} MB.`,
            }),
          );
        }
      }
    });

    if (acceptedFiles.length > 0) {
      setUploading(true);

      const promise = onDrop(acceptedFiles) || Promise.resolve();

      promise.then(() => {
        setUploading(false);
      });
    }
  };

  const { getRootProps, getInputProps, isDragAccept, isDragReject } =
    useDropzone({
      accept, // on windows we can get empty types easily, e.g. .md file
      multiple,
      maxSize: maxSizeMB * 1000 * 1000,
      onDrop: handleDrop,
    });

  let status;
  if (uploading) {
    status = UPLOADER_STATUS.UPLOADING;
  } else if (isDragAccept) {
    status = UPLOADER_STATUS.ACCEPT;
  } else if (isDragReject) {
    status = UPLOADER_STATUS.REJECT;
  } else {
    status = UPLOADER_STATUS.IDLE;
  }

  return (
    <div
      {...getRootProps()}
      className={classNames({ [styles.fullHeightContainer]: fullHeight })}
    >
      <input {...getInputProps()} />
      <BaseUploader
        image={image}
        status={status}
        subject={subject}
        info={info}
      />
    </div>
  );
};

export default Uploader;
